/**
 * Catalog -> nav mapping for the COMMERCIAL pipeline.
 *
 * The nav is driven by GET /api/pipeline/steps; this module holds only the
 * things an API payload cannot carry (icon components) or must not carry
 * (legacy data-testid values). Every lookup here is keyed with a FALLBACK, so a
 * step added to backend/pipeline_steps.py appears in the nav without editing
 * this file.
 *
 * COMMERCIAL ONLY (PIPELINE_DECISIONS.md decision 2). The residential nav group
 * is deliberately not routed through any of this.
 */
import {
  Activity, BarChart3, Circle, FileSpreadsheet, GitCompare, Leaf, Link2,
  ListChecks, ShieldCheck, Upload, Waves,
} from "lucide-react";

// Icons are React components, so they cannot come from the API. An unknown step
// key gets FALLBACK_STEP_ICON rather than rendering nothing.
export const STEP_ICONS = {
  dashboard: BarChart3,
  inventory_upload: Upload,
  energy_data_upload: FileSpreadsheet,
  retscreen_validation: ShieldCheck,
  iot_data: Activity,
  mb_pinn: Waves,
  data_comparison: GitCompare,
  consolidated_results: BarChart3,
  sire: ListChecks,
  eevc_co2e_report: Link2,
};

export const COMPONENT_ICONS = {
  eevc: Link2,
  co2e_emissions_report: Leaf,
};

export const FALLBACK_STEP_ICON = Circle;

export const stepIcon = (key) => STEP_ICONS[key] || FALLBACK_STEP_ICON;
export const componentIcon = (key) => COMPONENT_ICONS[key] || FALLBACK_STEP_ICON;

/**
 * Pre-existing data-testid values, preserved verbatim.
 *
 * The testing agent keys on these, so they must survive the nav becoming
 * catalog-driven — a generated id would have silently broken every existing
 * commercial nav assertion. New rows get a generated id instead.
 */
export const STEP_TESTIDS = {
  dashboard: "nav-commercial-dashboard",
  inventory_upload: "nav-commercial-inventory",
  energy_data_upload: "nav-commercial-energy-data",
  iot_data: "nav-commercial-iot",
  data_comparison: "nav-commercial-comparison",
  consolidated_results: "nav-commercial-consolidated",
};

export const COMPONENT_TESTIDS = {
  // Step 10's CO2e component is the existing, working CO2 report row.
  co2e_emissions_report: "nav-commercial-co2-report",
};

export const stepTestId = (key) => STEP_TESTIDS[key] || `nav-commercial-step-${key}`;
export const componentTestId = (key) =>
  COMPONENT_TESTIDS[key] || `nav-commercial-component-${key}`;

// Status -> the badge primitives already in index.css.
export const STATUS_BADGE_CLASS = {
  complete: "completed",
  ready: "in-progress",
  blocked: "blocked",
  not_started: "not-started",
};

export const statusBadgeClass = (status) => STATUS_BADGE_CLASS[status] || "not-started";

/**
 * A step's user-facing label.
 *
 * Prefers the `step_{key}` translation and falls back to the catalog `title`, so
 * a newly catalogued step reads correctly before its translations land instead
 * of showing a raw translation key. This is also how step 6 renders as
 * "Pulse Sync" while its key, routes and collections stay mb_pinn.
 */
export const stepLabel = (t, step) => {
  const key = `step_${step.key}`;
  const translated = t(key);
  return translated === key ? (step.title || step.key) : translated;
};

export const componentLabel = (t, component) => {
  const key = `step_component_${component.key}`;
  const translated = t(key);
  return translated === key ? (component.title || component.key) : translated;
};

/**
 * A step's label AS RENDERED IN THE NAV.
 *
 * Some catalog titles are too long for the 260px sidebar. `step_nav_{key}` is an
 * optional per-step short form used only here; with no such key the full
 * stepLabel is returned unchanged. This is display-only — the catalog title and
 * the detail pane keep the full label, so nothing keys off the short one.
 */
export const stepNavLabel = (t, step) => {
  const key = `step_nav_${step.key}`;
  const translated = t(key);
  return translated === key ? stepLabel(t, step) : translated;
};

/**
 * Merge per-asset status onto the catalog.
 *
 * THE CATALOG IS ITERATED, NOT THE STATUS RESPONSE. Order and membership come
 * from the catalog alone: a step with no status yet keeps its position with
 * default state, and the nav still renders in full when the status call fails
 * or when no asset is selected (the status endpoint requires an explicit
 * asset_uuid and will not guess one).
 */
export const mergePipelineSteps = (catalogSteps, statusSteps) => {
  const byKey = {};
  (statusSteps || []).forEach((s) => { byKey[s.key] = s; });

  return (catalogSteps || []).map((step) => {
    const status = byKey[step.key] || {};
    const components = (step.components || []).map((component) => {
      const statusComponent = ((status.components || []).find((c) => c.key === component.key)) || {};
      return { ...component, ...statusComponent };
    });
    return { ...step, ...status, components };
  });
};

/**
 * Cycle coverage of the cycle currently accumulating, as a percentage width.
 *
 * Read off the cycle record only — never synthesized. Clamped so a stored
 * record that overshoots cannot render a bar wider than its track.
 */
export const cycleProgressPercent = (cycle) => {
  const covered = Number(cycle?.months_covered) || 0;
  const required = Number(cycle?.months_required) || 0;
  if (required <= 0) return 0;
  return Math.max(0, Math.min(100, (covered / required) * 100));
};
